import React, { memo, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { SeriesGroup } from '../chart.typings'
import { SeriesGroupCategory } from '../../../pages/home'
import { selectChartConfigSettings, selectChartSeries } from '../../../selectors/chart'
import { getSecurityKey } from '../../../util/security'
import { selectIsEmbedded } from '../../../selectors/device'
import { IndicatorOutput_OutputType } from '../../../proto/generated/indicator'
import LiveText from '../../common/LiveText'
import LiveMarketText from '../../common/LiveMarketText'
import {
    buildSeriesMetadataLiveSubscribe,
    getComparableCloseSelector,
    getDayChangeReduxSelectors,
    getEmbeddedDayChangeReduxSelectors,
    getPrimaryBarChangeSelector,
    getPrimaryCloseSelector,
    getPrimaryHighSelector,
    getPrimaryLowSelector,
    getPrimaryOhlcColorSelector,
    getPrimaryOpenSelector,
    getPrimaryValueSelector,
    getStrikeDisplaySelector,
} from './seriesMetadataLiveDisplay'
import { getPrimaryMetadataFields } from './primaryMetadataDisplay'
import { ChartType } from '../../../proto/generated/chart'
import { getChartSnapshotSeriesGroup, hasChartCrosshairSnapshot } from '../chartSnapshotStore'
import { useChartCrosshairRevision } from '../chartCrosshairRevisionStore'
import { OI_OUTLOOK_INDICATOR_CODE } from '../../../assets/constants'
import IndicatorMetadataLiveValue from './IndicatorMetadataLiveValue'

export type SeriesMetadataLiveValuesProps = {
    viewKey: string
    seriesGroup: SeriesGroup
    screenerChart: boolean
    expressionMode: boolean
    derived: boolean
    showOptionExpiriesMenu: boolean
    isScalperEmbedded: boolean
    chartSecurityKey: string
    priceMultiplier: number
    /** Plot titles the indicator script actually drew on the last run. */
    activePlotTitles?: ReadonlySet<string>
}

const valueStyle = { fontSize: '13px' } as const

const StrikeValue = memo(function StrikeValue({
    viewKey,
    priceMultiplier,
    showOptionExpiriesMenu,
}: {
    viewKey: string
    priceMultiplier: number
    showOptionExpiriesMenu: boolean
}) {
    const strikeSelector = useMemo(
        () => getStrikeDisplaySelector(viewKey, priceMultiplier),
        [viewKey, priceMultiplier]
    )
    const subscribe = useMemo(() => buildSeriesMetadataLiveSubscribe(viewKey, ['primary']), [viewKey])

    if (!showOptionExpiriesMenu) return null

    return (
        <span className="series-meta-value-group">
            <span className="series-meta-label">Strike</span>
            <LiveText className="series-meta-value" style={valueStyle} subscribe={subscribe} selectText={strikeSelector} />
        </span>
    )
})

const SeriesMetadataLiveValues: React.FC<SeriesMetadataLiveValuesProps> = ({
    viewKey,
    seriesGroup,
    screenerChart,
    expressionMode,
    derived,
    showOptionExpiriesMenu,
    isScalperEmbedded,
    chartSecurityKey,
    priceMultiplier,
    activePlotTitles,
}) => {
    const { id, category, primary, series } = seriesGroup
    const seriesConfig = useSelector(selectChartSeries(viewKey))
    const chartSettingsStatusLine = useSelector(selectChartConfigSettings(viewKey))
    const isEmbedded = useSelector(selectIsEmbedded)
    useChartCrosshairRevision(viewKey)

    const isCrosshairActive = hasChartCrosshairSnapshot(viewKey)

    const [snapshotReady, setSnapshotReady] = useState(
        () => !!getChartSnapshotSeriesGroup(viewKey, id) || !!getChartSnapshotSeriesGroup(viewKey, 'primary')
    )

    const subscribe = useMemo(
        () => buildSeriesMetadataLiveSubscribe(viewKey, [id]),
        [viewKey, id]
    )

    useEffect(() => {
        if (snapshotReady) return
        return subscribe(() => {
            if (getChartSnapshotSeriesGroup(viewKey, id) || getChartSnapshotSeriesGroup(viewKey, 'primary')) {
                setSnapshotReady(true)
            }
        })
    }, [snapshotReady, subscribe, viewKey, id])

    const ohlcColorSelector = useMemo(() => getPrimaryOhlcColorSelector(viewKey), [viewKey])
    const openSelector = useMemo(() => getPrimaryOpenSelector(viewKey), [viewKey])
    const highSelector = useMemo(() => getPrimaryHighSelector(viewKey), [viewKey])
    const lowSelector = useMemo(() => getPrimaryLowSelector(viewKey), [viewKey])
    const closeSelector = useMemo(() => getPrimaryCloseSelector(viewKey), [viewKey])
    const valueSelector = useMemo(() => getPrimaryValueSelector(viewKey), [viewKey])
    const barChangeSelector = useMemo(() => getPrimaryBarChangeSelector(viewKey), [viewKey])

    const primarySeriesId = series[0]
    const primaryChartType = seriesConfig[primarySeriesId]?.chartType ?? ChartType.CANDLESTICK

    const primaryMetadataFields = useMemo(
        () => getPrimaryMetadataFields(primaryChartType, chartSettingsStatusLine),
        [primaryChartType, chartSettingsStatusLine]
    )

    const dayChangeSelectors = useMemo(
        () => (chartSecurityKey ? getDayChangeReduxSelectors(chartSecurityKey, expressionMode) : null),
        [chartSecurityKey, expressionMode]
    )

    const embeddedSelectors = useMemo(
        () =>
            chartSecurityKey
                ? getEmbeddedDayChangeReduxSelectors(viewKey, chartSecurityKey, expressionMode)
                : getEmbeddedDayChangeReduxSelectors(viewKey, '', expressionMode),
        [viewKey, chartSecurityKey, expressionMode]
    )

    if (category === SeriesGroupCategory.SERIES_DRAWING) {
        return null
    }
    if (!snapshotReady) {
        return null
    }

    if (primary) {
        const showEmbeddedDayView = isEmbedded && !isCrosshairActive

        if (showEmbeddedDayView) {
            // Outer span forces the values onto their own line; the inner
            // `.series-ohlc-container` shrink-wraps so its background chip only
            // covers the text (not the full row width).
            return (
                <span style={{ flexBasis: '100%', display: 'flex', minWidth: 0 }}>
                    <span className="series-ohlc-container">
                        <LiveText
                            className="series-meta-value"
                            style={valueStyle}
                            subscribe={subscribe}
                            selectText={embeddedSelectors.ltpText}
                            selectColor={embeddedSelectors.ltpColor}
                        />
                        <LiveMarketText
                            className="series-meta-value series-change-container"
                            style={valueStyle}
                            selectText={embeddedSelectors.changeText}
                            selectColor={embeddedSelectors.changeColor}
                        />
                    </span>
                </span>
            )
        }

        return (
            <span className="series-ohlc-container">
                {derived && (
                    <StrikeValue viewKey={viewKey} priceMultiplier={priceMultiplier} showOptionExpiriesMenu={showOptionExpiriesMenu} />
                )}
                {primaryMetadataFields.showOpen && (
                    <span className="series-meta-value-group">
                        <span className="series-meta-label">O</span>
                        <LiveText
                            className="series-meta-value"
                            style={valueStyle}
                            subscribe={subscribe}
                            selectText={openSelector}
                            selectColor={ohlcColorSelector}
                        />
                    </span>
                )}
                {primaryMetadataFields.showHigh && (
                    <span className="series-meta-value-group">
                        <span className="series-meta-label">H</span>
                        <LiveText
                            className="series-meta-value"
                            style={valueStyle}
                            subscribe={subscribe}
                            selectText={highSelector}
                            selectColor={ohlcColorSelector}
                        />
                    </span>
                )}
                {primaryMetadataFields.showLow && (
                    <span className="series-meta-value-group">
                        <span className="series-meta-label">L</span>
                        <LiveText
                            className="series-meta-value"
                            style={valueStyle}
                            subscribe={subscribe}
                            selectText={lowSelector}
                            selectColor={ohlcColorSelector}
                        />
                    </span>
                )}
                {primaryMetadataFields.showValue && (
                    <LiveText
                        className="series-meta-value"
                        style={valueStyle}
                        subscribe={subscribe}
                        selectText={valueSelector}
                        selectColor={ohlcColorSelector}
                    />
                )}
                {primaryMetadataFields.showClose && (
                    <span className="series-meta-value-group">
                        {primaryMetadataFields.closeLabel ? (
                            <span className="series-meta-label">{primaryMetadataFields.closeLabel}</span>
                        ) : null}
                        <LiveText
                            className="series-meta-value"
                            style={valueStyle}
                            subscribe={subscribe}
                            selectText={closeSelector}
                            selectColor={ohlcColorSelector}
                        />
                    </span>
                )}
                {primaryMetadataFields.showBarChange && (
                    <LiveText
                        className="series-meta-value series-change-container"
                        style={valueStyle}
                        subscribe={subscribe}
                        selectText={barChangeSelector}
                        selectColor={ohlcColorSelector}
                    />
                )}
                {!isEmbedded && chartSettingsStatusLine?.showLastDayChange && dayChangeSelectors && (
                    <LiveMarketText
                        className={undefined}
                        style={{ ...valueStyle, display: 'flex', alignItems: 'center' }}
                        selectText={dayChangeSelectors.text}
                        selectColor={dayChangeSelectors.color}
                    />
                )}
            </span>
        )
    }

    if (category === SeriesGroupCategory.SERIES_COMPARABLE_SECURITY) {
        const sid = series[0]
        const seriesConf = seriesConfig[sid]
        if (!seriesConf) return null
        const closeSelector = getComparableCloseSelector(viewKey, id, sid, seriesConf.priceFormat)
        return (
            <span className="series-ohlc-container">
                <LiveText
                    className="series-meta-value"
                    style={{ ...valueStyle, color: seriesConf.color }}
                    subscribe={subscribe}
                    selectText={closeSelector}
                />
            </span>
        )
    }

    const isOiOutlook = seriesGroup.scriptCode === OI_OUTLOOK_INDICATOR_CODE

    const visibleSeriesIds = useMemo(() => {
        const seriesIds = seriesGroup.series
        if (!activePlotTitles) {
            return seriesIds
        }
        return seriesIds.filter((sid) => {
            const title = seriesGroup.outputValues?.[sid]?.title
            return title != null && activePlotTitles.has(title)
        })
    }, [seriesGroup.series, seriesGroup.outputValues, activePlotTitles, activePlotTitles ? Array.from(activePlotTitles).join('\0') : ''])

    // In the mobile apps indicator/volume values are only shown while the
    // crosshair is active (mirrors the primary series' "day view" behaviour).
    if (isEmbedded && !isCrosshairActive) {
        return null
    }

    return (
        <span className="series-ohlc-container">
            {visibleSeriesIds.map((sid) => {
                const seriesConf = seriesConfig[sid]
                if (!seriesConf) return <React.Fragment key={sid} />

                const output = seriesGroup?.outputValues?.[sid]
                if (output?.type === IndicatorOutput_OutputType.FILL || output?.type === IndicatorOutput_OutputType.HZ_LINE) {
                    return <React.Fragment key={sid} />
                }

                if (category === SeriesGroupCategory.SERIES_VOLUME && isScalperEmbedded) {
                    return <React.Fragment key={sid} />
                }

                const staticColor =
                    category === SeriesGroupCategory.SERIES_VOLUME || isOiOutlook
                        ? undefined
                        : seriesConf.color

                const showValue = (chartSettingsStatusLine?.indicatorValues ?? true)

                return (
                    <IndicatorMetadataLiveValue
                        key={sid}
                        viewKey={viewKey}
                        seriesGroupId={id}
                        seriesId={sid}
                        priceFormat={seriesConf.priceFormat}
                        chartType={seriesConf.chartType}
                        staticColor={staticColor}
                        showValue={showValue}
                        withLabel={false}
                    />
                )
            })}
        </span>
    )
}

export default memo(SeriesMetadataLiveValues)
